import { useState, useEffect } from "react";
import { toast } from "sonner";
import ProtectedImage from "@/components/ProtectedImage";
import { apiClient, API_PREFIX } from "@/lib/api";

const API = API_PREFIX;

const PAGE_SIZE = 20;

// Filter options for quick search
const FILENAME_FILTERS = [
  { id: "all", label: "All Charts" },
  { id: "24h_compact_light", label: "24h Compact Light" },
  { id: "24h_simple_light", label: "24h Simple Light" },
  { id: "24h_simple_dark", label: "24h Simple Dark" },
  { id: "24h_light", label: "24h Light" },
  { id: "24h_dark", label: "24h Dark" },
  { id: "3day_light", label: "3-Day Light" },
  { id: "3day_dark", label: "3-Day Dark" },
];

export default function History() {
  const [history, setHistory] = useState({ total: 0, charts: [] });
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [selectedChart, setSelectedChart] = useState(null);
  const [filenameFilter, setFilenameFilter] = useState("all");

  const fetchHistory = async (skip = 0, append = false) => {
    try {
      if (append) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }
      const response = await apiClient.get(
        `${API}/charts/history?limit=${PAGE_SIZE}&skip=${skip}`,
      );
      if (append) {
        setHistory(prev => ({
          total: response.data.total,
          charts: [...prev.charts, ...response.data.charts]
        }));
      } else {
        setHistory(response.data);
      }
    } catch (error) {
      console.error("Failed to fetch history:", error);
      toast.error("Failed to load chart history");
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    fetchHistory();
  }, []);

  const handleLoadMore = () => {
    fetchHistory(history.charts.length, true);
  };

  const hasMore = history.charts.length < history.total;

  // Filter charts by filename pattern
  const filteredCharts = history.charts.filter(chart => {
    if (filenameFilter === "all") return true;
    const filename = chart.filename || "";
    switch (filenameFilter) {
      case "24h_compact_light":
        return filename.includes("24h_compact") && filename.includes("_light");
      case "24h_simple_light":
        return filename.includes("24h_simple") && filename.includes("_light");
      case "24h_simple_dark":
        return filename.includes("24h_simple") && !filename.includes("_light");
      case "24h_light":
        return filename.includes("24h") && !filename.includes("simple") && !filename.includes("compact") && filename.includes("_light");
      case "24h_dark":
        return filename.includes("24h") && !filename.includes("simple") && !filename.includes("compact") && !filename.includes("_light");
      case "3day_light":
        return filename.includes("3day") && filename.includes("_light");
      case "3day_dark":
        return filename.includes("3day") && !filename.includes("_light");
      default:
        return true;
    }
  });

  const handleRowClick = (chart) => {
    if (chart.status === 'success' && chart.image_url) {
      setSelectedChart(chart);
    }
  };

  const closeModal = () => {
    setSelectedChart(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#09090B] flex items-center justify-center">
        <div className="animate-spin h-8 w-8 border-2 border-zinc-600 border-t-zinc-100 rounded-full"></div>
      </div>
    );
  }

  return (
    <div data-testid="history-page" className="min-h-screen bg-[#09090B] p-4 md:p-6" style={{ background: '#09090B' }}>
      <header className="mb-6">
        <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-zinc-100">Chart History</h1>
        <p className="text-zinc-400 text-sm mt-1">
          Total charts generated: <span className="font-mono text-zinc-100">{history.total}</span>
          <span className="ml-4 text-zinc-600">• Click a row to view chart</span>
        </p>
      </header>

      {/* Filter Bar */}
      <div className="mb-4 flex flex-wrap gap-2">
        {FILENAME_FILTERS.map((filter) => (
          <button
            key={filter.id}
            onClick={() => setFilenameFilter(filter.id)}
            data-testid={`filter-${filter.id}`}
            className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
              filenameFilter === filter.id
                ? 'bg-amber-500 text-zinc-900'
                : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700 hover:text-zinc-200'
            }`}
          >
            {filter.label}
          </button>
        ))}
        {filenameFilter !== "all" && (
          <span className="text-zinc-500 text-xs self-center ml-2">
            Showing {filteredCharts.length} of {history.charts.length} loaded
          </span>
        )}
      </div>

      <div className="bg-[#18181B] border border-zinc-800 rounded-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-zinc-800">
                <th className="text-left text-xs uppercase tracking-wider text-zinc-500 font-medium px-4 py-3">Status</th>
                <th className="text-left text-xs uppercase tracking-wider text-zinc-500 font-medium px-4 py-3">Metal</th>
                <th className="text-left text-xs uppercase tracking-wider text-zinc-500 font-medium px-4 py-3">Size</th>
                <th className="text-left text-xs uppercase tracking-wider text-zinc-500 font-medium px-4 py-3">Generated At</th>
                <th className="text-left text-xs uppercase tracking-wider text-zinc-500 font-medium px-4 py-3">Price</th>
                <th className="text-left text-xs uppercase tracking-wider text-zinc-500 font-medium px-4 py-3">Change</th>
                <th className="text-left text-xs uppercase tracking-wider text-zinc-500 font-medium px-4 py-3">Filename</th>
              </tr>
            </thead>
            <tbody>
              {filteredCharts.length === 0 ? (
                <tr>
                  <td colSpan={7} className="text-center text-zinc-500 py-8">
                    {history.charts.length === 0 ? "No charts generated yet" : "No charts match the filter"}
                  </td>
                </tr>
              ) : (
                filteredCharts.map((chart) => (
                  <tr 
                    key={chart.id} 
                    data-testid={`history-row-${chart.id}`} 
                    className={`border-b border-zinc-800 transition-colors ${
                      chart.status === 'success' 
                        ? 'hover:bg-zinc-800/50 cursor-pointer' 
                        : 'opacity-60'
                    }`}
                    onClick={() => handleRowClick(chart)}
                  >
                    <td className="px-4 py-3">
                      <span className={`text-xs border rounded px-2 py-0.5 ${chart.status === 'success' ? 'text-emerald-500 border-emerald-500/30' : 'text-red-500 border-red-500/30'}`}>
                        {chart.status === 'success' ? 'Success' : 'Failed'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm font-mono text-amber-500">{chart.metal}</td>
                    <td className="px-4 py-3 text-xs font-mono text-zinc-400">{chart.size || 'large'}</td>
                    <td className="px-4 py-3 text-sm font-mono text-zinc-100">{new Date(chart.generated_at).toLocaleString()}</td>
                    <td className="px-4 py-3 text-sm font-mono text-zinc-100">
                      {chart.stats ? `$${chart.stats.current_price.toFixed(2)}` : '-'}
                    </td>
                    <td className="px-4 py-3 text-sm font-mono">
                      {chart.stats ? (
                        <span className={chart.stats.change >= 0 ? 'text-emerald-500' : 'text-red-500'}>
                          {chart.stats.change >= 0 ? '+' : ''}{chart.stats.change.toFixed(2)} ({chart.stats.change_percent >= 0 ? '+' : ''}{chart.stats.change_percent.toFixed(2)}%)
                        </span>
                      ) : '-'}
                    </td>
                    <td className="px-4 py-3 text-xs font-mono text-zinc-500 truncate max-w-[200px]">{chart.filename || '-'}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Load More Button */}
      {hasMore && (
        <div className="flex justify-center mt-6">
          <button
            onClick={handleLoadMore}
            disabled={loadingMore}
            data-testid="load-more-btn"
            className="px-6 py-2.5 bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 hover:border-zinc-600 text-zinc-200 text-sm font-medium rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {loadingMore ? (
              <>
                <div className="animate-spin h-4 w-4 border-2 border-zinc-500 border-t-zinc-200 rounded-full"></div>
                Loading...
              </>
            ) : (
              <>
                Load More
                <span className="text-zinc-500 text-xs">({history.charts.length} of {history.total})</span>
              </>
            )}
          </button>
        </div>
      )}

      {/* Show message when all loaded */}
      {!hasMore && history.charts.length > 0 && (
        <div className="text-center mt-6 text-zinc-500 text-sm">
          All {history.total} charts loaded
        </div>
      )}

      {/* Chart Preview Modal - Just the chart image */}
      {selectedChart && (
        <div 
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
          onClick={closeModal}
        >
          <div 
            className="relative max-w-6xl w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <button 
              onClick={closeModal}
              className="absolute -top-10 right-0 text-zinc-400 hover:text-zinc-100 transition-colors p-2"
              data-testid="close-modal-btn"
            >
              <svg className="h-8 w-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <ProtectedImage
              src={`${selectedChart.image_url}${selectedChart.generated_at ? `?v=${encodeURIComponent(selectedChart.generated_at)}` : ""}`}
              alt={`${selectedChart.metal} Chart`}
              className="w-full h-auto"
              data-testid="modal-chart-image"
              errorFallback={
                <div className="w-full min-h-[320px] flex items-center justify-center text-zinc-500 text-sm">
                  Unable to load chart preview
                </div>
              }
            />
          </div>
        </div>
      )}
    </div>
  );
}
